import { useState, useCallback, useRef } from 'react';
import { useConditionalEffect } from '@react-hookz/web';
import { gsap } from 'gsap';
/**
 * Use Ref Hook (with callbacks),
 *
 * @param {Array.<string>} processNode Function to reduce the node object
 *
 * @example
 * // let [node, ref] = useStateRefs((node) => (node?.clientHeight || 0))
 * let [node, ref] = useStateRefs()
 * console.log(node)
 * return <div ref={ref} />
 */

var useStateRef = function useStateRef(processNode) {
  var _useState = useState(null),
      node = _useState[0],
      setNode = _useState[1];

  var setRef = useCallback(function (newNode) {
    return setNode(processNode ? processNode(newNode) : newNode);
  }, [processNode]);
  return [node, setRef];
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}
/**
 * Use Refs Hook (with callbacks),
 * generates an bject containing multiple ref.
 *
 * @param {Array.<string>} keys An array containing to keys of the returned object
 *
 * @example
 * let refs = useStateRefs(['head', 'content', 'overlay'])
 * console.log(refs.head.current)
 * return <div ref={refs.head.ref} />
 */


var useStateRefs = function useStateRefs(keys) {
  if (keys === void 0) {
    keys = [];
  }

  return keys.reduce(function (acc, i) {
    var _extends2; // eslint-disable-next-line react-hooks/rules-of-hooks


    var node = useStateRef();
    return _extends({}, acc, (_extends2 = {}, _extends2[i] = {
      ref: node[1],
      current: node[0]
    }, _extends2));
  }, {});
};
/**
 * Use Refs Hook,
 * generates an bject containing multiple ref.
 *
 * @param {Array.<string>} keys An array containing to keys of the returned object
 *
 * @example
 * let refs = useRefs(['head', 'content', 'overlay'])
 * return <div ref={refs.head} />
 */
// eslint-disable-next-line react-hooks/rules-of-hooks


var useRefs = function useRefs(keys) {
  if (keys === void 0) {
    keys = [];
  }

  return keys.reduce(function (acc, i) {
    var _extends2;

    return _extends({}, acc, (_extends2 = {}, _extends2[i] = useRef(null), _extends2));
  }, {});
};
/**
 * Use GSAP Tween / Timeline,
 * convenient hook to use Tweens & Timelines with React handlers.
 *
 * @param {function} instance The GSAP Tween/Timeline, accepts function and renders.
 * @param {Array} predicates All predicates must be true, for the effect to run.
 * @param {Array} deps The effect dependencies.
 *
 * @property  {function} instance.gsap Expose `gsap` as a prop
 * @property  {Object} instance.node Expose the ref.current
 * @property  {function} instance.$  (node) => node.current
 *
 * @example
 *
 * const [tween, tweenRef] = useTween(({ gsap, ref }) =>
 *  gsap.to(ref, {
 *    duration: 2,
 *    backgroundColor: '#999',
 *    paused: true,
 *  })
 * )
 * const onMouseEnterHandler = () => tween.play()
 * const onMouseLeaveHandler = () => tween.reverse()
 * ...
 *
 * const [tl, tlRef] = useTimeline(({ gsap, $  }) =>
 *  gsap
 *    .timeline({ paused: true, duration: 2, })
 *    .to($(someRef), { backgroundColor: '#999' })
 *    .to($(otherRef), { rotation: 45 })
 * )
 * const onMouseEnterHandler = () => timeline.play()
 * const onMouseLeaveHandler = () => timeline.reverse()
 */


var useTween = function useTween(instance, deps, predicates, passedRef) {
  if (deps === void 0) {
    deps = [];
  }

  if (predicates === void 0) {
    predicates = [];
  }

  var _useStateRef = useStateRef(),
      node = _useStateRef[0],
      ref = _useStateRef[1];

  var tween = useRef(null);
  var main = passedRef ? passedRef.current : node;
  useConditionalEffect(function () {
    if (typeof instance === 'function') {
      tween.current = instance({
        gsap: gsap,
        node: main,
        $: function $(obj) {
          return obj == null ? void 0 : obj.current;
        }
      });
    } else {
      tween.current = instance;
    }
  }, [main].concat(deps), [main != null].concat(predicates));
  return [tween.current, {
    ref: ref,
    current: node
  }];
};
/** @see https://codesandbox.io/s/react-hover-forward-ref-ybtgs?file=/src/App.js:354-366 */

/**
 * Set of GSAP event that wraps timelines with `will-change`.
 *
 * @param {Object} ref The ref of the target
 * @param {string} willChange The will-change value
 *
 * @example gsap.timeline({ defaults: {}, ...willChangeEvents(ref, 'height') })
 */


var willChangeEvents = function willChangeEvents(ref, willChange) {
  if (willChange === void 0) {
    willChange = 'auto';
  }

  var end = [ref, {
    clearProps: 'will-change'
  }];
  return {
    onUpdate: function onUpdate() {
      return gsap.set(ref, {
        willChange: willChange
      });
    },
    onComplete: function onComplete() {
      return gsap.set.apply(gsap, end);
    },
    onReverseComplete: function onReverseComplete() {
      return gsap.set.apply(gsap, end);
    }
  };
};

export { useRefs, useStateRef, useStateRefs, useTween as useTimeline, useTween, willChangeEvents };